import {
  type ExtendedChordQuality,
  type InversionType,
  type ExplorerFilter,
  getChordDef,
} from "./chordEngine";
import { noteToSemitones, semitonesToNote, shouldUseFlats, type NoteName } from "./guitarEngine";
import type { PracticeNoteEvent } from "./practiceNoteModel";

function applyInversion(
  inv: InversionType,
  intervals: number[],
  degrees: string[]
): { intervals: number[]; degrees: string[] } {
  switch (inv) {
    case "first":
      return {
        intervals: [...intervals.slice(1), intervals[0] + 12],
        degrees: [...degrees.slice(1), degrees[0]],
      };
    case "second":
      return {
        intervals: [...intervals.slice(2), intervals[0] + 12, intervals[1] + 12],
        degrees: [...degrees.slice(2), degrees[0], degrees[1]],
      };
    case "third":
      return {
        intervals: [...intervals.slice(3), intervals[0] + 12, intervals[1] + 12, intervals[2] + 12],
        degrees: [...degrees.slice(3), degrees[0], degrees[1], degrees[2]],
      };
    default:
      return { intervals, degrees };
  }
}

function applyPianoVoicingSpread(
  midis: number[],
  type: ExplorerFilter["voicingType"]
): number[] {
  if (midis.length <= 2 || type === "all") return midis;
  const sorted = [...midis].sort((a, b) => a - b);

  if (type === "spread") {
    return sorted.map((m, i) => m + (i % 2 === 1 ? 12 : 0));
  }

  if (type === "drop2" && sorted.length >= 4) {
    const next = [...sorted];
    const idx = next.length - 2;
    next[idx] = next[idx] - 12;
    return next.sort((a, b) => a - b);
  }

  if (type === "drop3" && sorted.length >= 4) {
    const next = [...sorted];
    const idx = next.length - 3;
    next[idx] = next[idx] - 12;
    return next.sort((a, b) => a - b);
  }

  return sorted;
}

export function inversionOptionsForChord(noteCount: number): InversionType[] {
  if (noteCount >= 4) return ["fundamental", "first", "second", "third"];
  if (noteCount === 3) return ["fundamental", "first", "second"];
  return ["fundamental"];
}

export function buildChordToneMidis(
  root: NoteName,
  quality: ExtendedChordQuality,
  inversion: InversionType,
  voicingType: ExplorerFilter["voicingType"]
): { midi: number; degree: string; step: NoteName }[] {
  const def = getChordDef(quality);
  if (!def) return [];

  const rootSemi = noteToSemitones(root);
  const rootMidi = 60 + rootSemi;
  const { intervals, degrees } = applyInversion(inversion, def.intervals, def.degreeNames);
  const useFlats = shouldUseFlats(
    root,
    quality.includes("m") && !quality.includes("maj") ? "minor" : "major"
  );

  const midis = intervals.map((int, i) => ({
    midi: rootMidi + int,
    degree: degrees[i] ?? "R",
    step: semitonesToNote(rootSemi + int, useFlats),
  }));

  const spreadMidis = applyPianoVoicingSpread(
    midis.map((m) => m.midi),
    voicingType
  );

  return spreadMidis.map((midi) => {
    const match =
      midis.find((m) => m.midi === midi) ??
      midis.find((m) => ((m.midi % 12) + 12) % 12 === ((midi % 12) + 12) % 12);
    return {
      midi,
      degree: match?.degree ?? "R",
      step: semitonesToNote(midi, useFlats),
    };
  });
}

export function buildChordStaffEvents(
  root: NoteName,
  quality: ExtendedChordQuality,
  inversion: InversionType,
  voicingType: ExplorerFilter["voicingType"]
): PracticeNoteEvent[] {
  return buildChordToneMidis(root, quality, inversion, voicingType).map((t, i) => ({
    midi: t.midi,
    start: i * 480,
    duration: 480,
    hand: "R",
    degree: t.degree,
    step: t.step,
  }));
}
